/**
 * SecurityCheckScreen.tsx
 * Contrôle sécurité véhicule — étape entre Carburant et Signatures
 *
 * @version 2.0.0 — Dynamique via Backoffice & Code Catégorie MRC
 */

import { useState, useEffect, useMemo } from 'react'
import { useAppStore } from '../store/useAppStore'
import { useChecklistStore, ChecklistItem } from '../store/useChecklistStore'
import Header from '../components/ui/Header'
import Button from '../components/ui/Button'
import Card   from '../components/ui/Card'
import toast  from 'react-hot-toast'
import { ShieldCheck, AlertTriangle, Loader2 } from 'lucide-react'

type CheckState = 'idle' | 'ok' | 'missing'

export default function SecurityCheckScreen() {
  const inspection       = useAppStore(s => s.inspection)
  const updateInspection = useAppStore(s => s.updateInspection)
  const setStep          = useAppStore(s => s.setStep)

  // Store Checklist
  const { checklistItems, fetchItems, itemsLoading } = useChecklistStore()

  // Déterminer la catégorie MyRentCar (ex: 03F, AE)
  const contractCategory = (inspection?.contract as any)?.category || ''

  // ── State local ────────────────────────────────────────────────────────────
  const [checks, setChecks]       = useState<Record<string, CheckState>>({})
  const [comments, setComments]       = useState<Record<string, string>>({})
  const [confirmed, setConfirmed]     = useState(false)

  // Charger les items au montage
  useEffect(() => {
    fetchItems()
  }, [])

  // Restaurer depuis inspection si déjà rempli
  useEffect(() => {
    const saved = inspection?.securityCheck
    if (saved) {
      setChecks(saved.checks ?? {})
      setComments(saved.comments ?? {})
      setConfirmed(saved.confirmed ?? false)
    }
  }, [])

  // ── Filtrage Dynamique ──────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    if (!checklistItems.length) return []
    return checklistItems.filter(item => {
      // 1. Si c'est un item de "Base", on l'affiche toujours
      if (item.is_base) return true
      
      // 2. Sinon, on vérifie si la catégorie du contrat est dans la liste de l'item
      if (!contractCategory) return false
      return item.category_codes.some(c => c.toUpperCase() === contractCategory.toUpperCase())
    })
  }, [checklistItems, contractCategory])

  const allAnswered = filtered.length > 0 && filtered.every(i => checks[i.id] && checks[i.id] !== 'idle')
  const hasMissing  = filtered.some(i => checks[i.id] === 'missing')
  const canValidate = allAnswered && confirmed

  const setCheck = (id: string, val: CheckState) => {
    setChecks(prev => ({ ...prev, [id]: val }))
  }

  const handleValidate = () => {
    if (!canValidate) return

    const securityCheck = {
      category: contractCategory,
      checks,
      comments,
      confirmed,
      validatedAt: new Date().toISOString(),
    }

    updateInspection({ securityCheck })
    toast.success('Contrôle sécurité validé ✓')
    setStep('signatures')
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header 
        title="Contrôle Sécurité" 
        subtitle={contractCategory ? `Catégorie : ${contractCategory}` : "Vérification des équipements"} 
        showBack 
        backStep="fuel" 
      />

      <div className="flex-1 p-4 max-w-lg mx-auto w-full flex flex-col gap-4 pb-32">

        {/* Loading State */}
        {itemsLoading && !checklistItems.length && (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400 gap-3">
            <Loader2 className="animate-spin" size={32} />
            <p className="text-sm">Chargement de la checklist…</p>
          </div>
        )}

        {/* Message de responsabilité */}
        <div className="bg-amber-50 border border-amber-300 rounded-2xl p-4 flex gap-3 shadow-sm">
          <AlertTriangle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-amber-800 mb-1">Votre responsabilité est engagée</p>
            <p className="text-sm text-amber-700 leading-relaxed">
              Cette partie de l'état des lieux est <strong>sous votre responsabilité</strong>. 
              Vérifiez chaque point avec attention avant de valider.
            </p>
          </div>
        </div>

        {/* Checklist */}
        <div className="space-y-3">
          {filtered.map(item => {
            const state = checks[item.id] || 'idle'
            return (
              <div
                key={item.id}
                className={`bg-white rounded-2xl shadow-sm p-4 border-l-4 transition-all ${
                  state === 'ok'      ? 'border-green-500' :
                  state === 'missing' ? 'border-red-500'   :
                  'border-gray-200'
                }`}
              >
                {/* Question */}
                <div className="flex items-start gap-3 mb-3">
                  <span className="text-2xl">{item.icon}</span>
                  <p className="text-sm font-medium text-gray-700 leading-snug flex-1">
                    {item.question}
                  </p>
                </div>

                {/* Boutons Oui / Non */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setCheck(item.id, 'ok')}
                    className={`flex-1 py-3 rounded-xl text-xs font-bold border transition-all ${
                      state === 'ok'
                        ? 'bg-green-500 text-white border-green-500 shadow-lg shadow-green-100'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-green-400'
                    }`}
                  >
                    ✅ Oui, présent
                  </button>
                  <button
                    onClick={() => setCheck(item.id, 'missing')}
                    className={`flex-1 py-3 rounded-xl text-xs font-bold border transition-all ${
                      state === 'missing'
                        ? 'bg-red-500 text-white border-red-500 shadow-lg shadow-red-100'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-red-400'
                    }`}
                  >
                    ❌ Absent
                  </button>
                </div>

                {/* Commentaire si manquant */}
                {state === 'missing' && (
                  <div className="mt-3">
                    <textarea
                      placeholder="Précisez la situation..."
                      value={comments[item.id] || ''}
                      onChange={e => setComments(prev => ({ ...prev, [item.id]: e.target.value }))}
                      className="w-full text-sm border-2 border-red-100 rounded-xl p-2 resize-none focus:outline-none focus:ring-2 focus:ring-red-300"
                      rows={2}
                    />
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Alerte si manquants */}
        {hasMissing && (
          <div className="bg-red-50 border border-red-300 rounded-2xl p-4 flex gap-3 animate-in fade-in slide-in-from-bottom-2">
            <span className="text-xl">🚨</span>
            <p className="text-sm text-red-700 font-medium">
              Des éléments sont manquants. Signalez-le avant de remettre le véhicule au client.
            </p>
          </div>
        )}

        {/* Case à cocher confirmation */}
        {allAnswered && (
          <div
            onClick={() => setConfirmed(p => !p)}
            className={`bg-white rounded-2xl shadow-sm p-4 flex items-start gap-3 cursor-pointer border-2 transition-all ${
              confirmed ? 'border-blue-500 bg-blue-50/30' : 'border-gray-100'
            }`}
          >
            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
              confirmed ? 'bg-blue-600 border-blue-600' : 'border-gray-400'
            }`}>
              {confirmed && <span className="text-white text-xs font-bold">✓</span>}
            </div>
            <p className="text-sm text-gray-700 leading-snug">
              Je certifie avoir vérifié tous les équipements de sécurité listés ci-dessus.
            </p>
          </div>
        )}

        {/* 🛠️ DEBUG INFO (Visible temporairement pour debug) */}
        <div className="mt-8 p-3 bg-gray-100 rounded-xl border border-gray-200 opacity-50">
           <p className="text-[10px] font-mono text-gray-400">
             DEBUG: Category="{contractCategory}" | Items={filtered.length} | Total={checklistItems.length}
           </p>
        </div>
      </div>

      {/* CTA fixe en bas */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white/80 backdrop-blur-md border-t border-gray-100 max-w-lg mx-auto z-10">
        <Button
          onClick={handleValidate}
          disabled={!canValidate}
          size="xl"
          className="w-full shadow-xl"
        >
          <ShieldCheck size={18} />
          {canValidate ? 'Valider et continuer →' : 'Répondez à toutes les questions'}
        </Button>
      </div>
    </div>
  )
}
